interface Props {
  id: string;
  label: string;
  type?: 'text' | 'email' | 'tel' | 'url' | 'number';
  value: string | number;
  onChange: (value: string) => void;
  onBlur?: () => void;
  error?: string;
  placeholder?: string;
  required?: boolean;
  maxLength?: number;
  disabled?: boolean;
  hint?: string;
  multiline?: boolean;
  rows?: number;
}

export default function FormInput({
  id,
  label,
  type = 'text',
  value,
  onChange,
  onBlur,
  error,
  placeholder,
  required,
  maxLength,
  disabled,
  hint,
  multiline,
  rows = 4,
}: Props) {
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;
  const hasError = Boolean(error);
  const hasHint = Boolean(hint) && !hasError;

  // Build aria-describedby based on what's present
  const describedBy = [
    hasError ? errorId : null,
    hasHint ? hintId : null,
  ].filter(Boolean).join(' ') || undefined;

  const inputClassName = `
    w-full px-3 py-2 bg-surface-app-elevated border rounded-lg text-ink-primary
    placeholder-ink-subtle focus:outline-none focus:ring-2
    transition-colors
    ${error
      ? 'border-brand-warm focus:ring-red-500/50'
      : 'border-border-strong focus:ring-brand-primary/50 focus:border-brand-primary'
    }
    ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
  `;

  return (
    <div className="space-y-1">
      <label htmlFor={id} className="block text-sm font-medium text-ink-primary">
        {label}
        {required && <span className="text-brand-warm-deep ml-1" aria-hidden="true">*</span>}
      </label>
      {multiline ? (
        <textarea
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          placeholder={placeholder}
          maxLength={maxLength}
          disabled={disabled}
          required={required}
          rows={rows}
          aria-invalid={hasError}
          aria-describedby={describedBy}
          className={`${inputClassName} resize-y min-h-[100px]`}
        />
      ) : (
        <input
          id={id}
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          placeholder={placeholder}
          maxLength={maxLength}
          disabled={disabled}
          required={required}
          aria-invalid={hasError}
          aria-describedby={describedBy}
          className={inputClassName}
        />
      )}
      {hasHint && (
        <p id={hintId} className="text-xs text-ink-muted">{hint}</p>
      )}
      {hasError && (
        <p id={errorId} className="text-sm text-brand-warm-deep" role="alert">{error}</p>
      )}
    </div>
  );
}
